/**
 * Sticky Save/Discard bar for a catalogue whose ordering did NOT autosave.
 *
 * Reordering normally persists silently 500ms after the last move; this bar is
 * the failure path only, so its presence is meaningful — if you can see it, the
 * order on screen is not the order in the database.
 */

import React from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { colors } from '../../../theme/designTokens';

export interface UnsavedOrderBarProps {
  visible: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

const UnsavedOrderBar: React.FC<UnsavedOrderBarProps> = ({ visible, saving, onSave, onDiscard }) => {
  if (!visible) return null;

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#0d3d50',
        color: '#fff',
        px: 3,
        py: 1.5,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.2)',
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontFamily: '"Montserrat", sans-serif', fontSize: '0.82rem' }}
      >
        Order could not be saved automatically — this order is not live yet
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<UndoIcon />}
          onClick={onDiscard}
          disabled={saving}
          sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: '#fff' } }}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving}
          sx={{
            bgcolor: colors.warmGold[500],
            '&:hover': { bgcolor: colors.warmGold[600] },
          }}
        >
          {saving ? 'Saving...' : 'Save Order'}
        </Button>
      </Box>
    </Box>
  );
};

export default UnsavedOrderBar;
